import { AuthSelectors, useAppSelector } from '@/stores';
import { useEffect, useRef } from 'react';

import { EventSourcePolyfill } from 'event-source-polyfill';

/** Event payload sent by the backend (TabletStreamRegistry.publish). */
export type TabletListEventType =
	| 'TABLET_REGISTERED'
	| 'TABLET_REMOTE_ACTIVATED'
	| 'TABLET_ACTIVATED'
	| 'TABLET_DEACTIVATED'
	| 'TABLET_DELETED';

export interface TabletListEvent {
	eventType: TabletListEventType | string;
	tabletName: string | null;
}

/**
 * Subscribe to the backend SSE stream of tablet-list change events. Whenever
 * the server pushes a "tablet-list" event (admin or device action that flipped
 * tablet status, deletion, REMOTE re-activation, etc.), the supplied callback
 * fires with the parsed payload so the caller can react differently — e.g.
 * close the register-code modal only when the matching tablet has registered
 * (so concurrent admins of the same unit don't lose their unrelated modals).
 *
 * Uses event-source-polyfill because the native browser EventSource cannot set
 * custom headers (Authorization). The polyfill speaks the same protocol but
 * runs over XHR/fetch underneath.
 *
 * Auto-reconnect with exponential backoff (1s → 30s) on transport errors.
 * Reconnects with the latest JWT whenever the token in the Redux store changes
 * (e.g. after /auth/refresh).
 */
export const useTabletListStream = (
	onEvent: (event: TabletListEvent) => void
): void => {
	const accessToken = useAppSelector(AuthSelectors.selectAccessToken);
	const onEventRef = useRef(onEvent);

	// Keep the latest callback in a ref so reconnect logic doesn't recreate the
	// EventSource every time the parent's callback identity changes.
	useEffect(() => {
		onEventRef.current = onEvent;
	}, [onEvent]);

	useEffect(() => {
		if (!accessToken) return;

		let es: EventSourcePolyfill | null = null;
		let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
		let backoffMs = 1000;
		let cancelled = false;

		const connect = (): void => {
			if (cancelled) return;
			const url = `${process.env.REACT_APP_API_URL}/worker/device/tablet/stream`;
			// eslint-disable-next-line no-console
			console.log('[SSE] connecting:', url);
			es = new EventSourcePolyfill(url, {
				headers: { Authorization: `Bearer ${accessToken}` },
				// Heartbeat from server every 30s — give 60s grace before the
				// polyfill considers the connection dead.
				heartbeatTimeout: 60_000,
			});

			es.addEventListener('open', () => {
				// eslint-disable-next-line no-console
				console.log('[SSE] open');
			});

			es.addEventListener('connected', () => {
				// eslint-disable-next-line no-console
				console.log('[SSE] connected event from server');
			});

			es.addEventListener('tablet-list', (ev: any) => {
				const raw: string = ev?.data ?? '';
				// Backend sends JSON {eventType, tabletName} (newer) or a bare event
				// type string (older). Accept both so a stale BE doesn't kill the FE.
				let parsed: TabletListEvent = { eventType: raw, tabletName: null };
				const trimmed = raw.trim();
				if (trimmed.startsWith('{')) {
					try {
						const obj = JSON.parse(trimmed);
						parsed = {
							eventType:
								typeof obj?.eventType === 'string' ? obj.eventType : '',
							tabletName:
								typeof obj?.tabletName === 'string' ? obj.tabletName : null,
						};
					} catch {
						// Stick with the raw-string fallback above.
					}
				}
				// eslint-disable-next-line no-console
				console.log('[SSE] tablet-list parsed:', parsed, 'raw:', raw);
				onEventRef.current(parsed);
				backoffMs = 1000; // Reset on successful event
			});

			es.onerror = (ev: any): void => {
				es?.close();
				es = null;
				if (cancelled) return;

				// Auth failure (expired/invalid JWT): do NOT reconnect with the same
				// stale token. Blind retries here loop forever every ~30s, each one
				// hitting the backend's expired-token handler — which previously
				// wiped the account's token_storage rows and locked the user out.
				// The effect depends on [accessToken], so once the app refreshes the
				// token (Redux updates) this hook reconnects automatically.
				const status = ev?.status;
				if (status === 401 || status === 403) {
					// eslint-disable-next-line no-console
					console.warn(
						'[SSE] auth error',
						status,
						'- stop retrying with stale token'
					);
					return;
				}

				// eslint-disable-next-line no-console
				console.warn('[SSE] error, will reconnect in', backoffMs, 'ms', ev);
				reconnectTimer = setTimeout(connect, backoffMs);
				backoffMs = Math.min(backoffMs * 2, 30_000);
			};
		};

		connect();

		return (): void => {
			cancelled = true;
			if (reconnectTimer) clearTimeout(reconnectTimer);
			es?.close();
		};
	}, [accessToken]);
};
